// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
import * as React from 'react';
import { ChevronDown, ChevronUp, Search, SlidersHorizontal } from 'lucide-react';

import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { CatalogEntryDto } from '@greenlight/shared';

import { FABRIC_FOLDED_CATALOG_KEYS } from './fabricRegistration';

/**
 * The eight entries shown before any search — the most-registered vendors, in
 * curated order. The rest are one click away via Show all; search spans the whole
 * catalog either way.
 */
const FEATURED_CATALOG_KEYS = [
  'openai',
  'anthropic',
  'microsoft-teams',
  'jira',
  'zoom',
  'google-workspace',
  'salesforce',
  'tableau',
] as const;

export interface CatalogPickerProps {
  entries: CatalogEntryDto[];
  isLoading: boolean;
  isError: boolean;
  onSelect: (entry: CatalogEntryDto) => void;
  onCustom: () => void;
}

/**
 * The registration wizard's first step (ADR-0026, docs/28 § /integrations): a
 * searchable **grid** of catalog entries (logo + name), plus a Custom escape hatch.
 * Picking an entry advances to the prefilled form. Catalog order is the curated seed
 * order (popular first); no category chrome.
 */
export function CatalogPicker({
  entries,
  isLoading,
  isError,
  onSelect,
  onCustom,
}: CatalogPickerProps): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const [showAll, setShowAll] = React.useState(false);
  const searching = query.trim() !== '';

  const pickable = React.useMemo(
    () => entries.filter((e) => !FABRIC_FOLDED_CATALOG_KEYS.has(e.catalog_key)),
    [entries],
  );
  const matched = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q !== '') {
      return pickable.filter(
        (e) =>
          e.display_name.toLowerCase().includes(q) ||
          e.catalog_key.includes(q) ||
          e.category.replace(/-/g, ' ').includes(q) ||
          e.search_keywords.some((keyword) => keyword.toLowerCase().includes(q)),
      );
    }
    if (showAll) return pickable;
    return FEATURED_CATALOG_KEYS.map((key) => entries.find((e) => e.catalog_key === key)).filter(
      (e): e is CatalogEntryDto => e !== undefined,
    );
  }, [pickable, entries, query, showAll]);

  // The featured grid is short enough to sit in the flow; the full catalog and a
  // broad search are not, and a modal that grows past the viewport buries the
  // Custom escape hatch below the fold.
  const scrolls = (showAll || searching) && matched.length > 8;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="catalog-search">Search the integration catalog</Label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="catalog-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. GitHub, Snowflake, market data…"
            className="pl-9"
          />
        </div>
      </div>

      {isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            Couldn&apos;t load the integration catalog. You can still register a Custom integration.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" aria-hidden="true">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-[88px] w-full rounded-gl" />
          ))}
        </div>
      ) : matched.length === 0 ? (
        <p className="px-1 text-[13px] text-muted-foreground">
          No catalogued integration matches “{query}”. Use the Custom path below.
        </p>
      ) : (
        <div
          className={cn(
            'grid grid-cols-3 gap-2 sm:grid-cols-4',
            scrolls && 'max-h-[19rem] overflow-y-auto pr-1',
          )}
          {...(scrolls ? { tabIndex: 0, role: 'group', 'aria-label': 'Integration catalog' } : {})}
        >
          {matched.map((entry) => {
            // An entry with no MVP-selectable profile (e.g. Snowflake — user-delegated
            // only) is catalogued but not yet registerable (ADR-0026 §3): show it
            // disabled rather than as a dead click.
            const selectable = entry.connection_profiles.some((p) => p.mvp_selectable);
            return (
              <button
                key={entry.catalog_key}
                type="button"
                onClick={() => onSelect(entry)}
                disabled={!selectable}
                title={entry.display_name}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-gl border border-border p-3 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selectable ? 'hover:bg-accent/50' : 'cursor-not-allowed opacity-60',
                )}
                data-testid={`catalog-entry-${entry.catalog_key}`}
              >
                <IntegrationTile
                  slug={entry.catalog_key}
                  catalogKey={entry.catalog_key}
                  displayName={entry.display_name}
                  size={44}
                />
                <span className="line-clamp-2 min-h-[2.4em] w-full text-[11.5px] font-medium leading-tight text-foreground">
                  {entry.display_name}
                </span>
                {!selectable && <span className="text-[10px] text-muted-foreground">Soon</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Without this the rest of the catalog is reachable only by already knowing
          the vendor's name — the featured eight are a shortcut, not the inventory. */}
      {!searching && !isLoading && !isError && pickable.length > FEATURED_CATALOG_KEYS.length && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          aria-expanded={showAll}
          data-testid="catalog-show-all"
          className="inline-flex items-center gap-1.5 rounded-gl px-1 py-1 text-[12.5px] font-medium text-link transition-colors hover:text-link/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {showAll ? (
            <>
              Show fewer
              <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
            </>
          ) : (
            <>
              Show all {pickable.length} integrations
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </>
          )}
        </button>
      )}

      {/* Custom escape hatch — always available, even with no catalog match. */}
      <button
        type="button"
        onClick={onCustom}
        data-testid="catalog-custom"
        className="flex w-full items-center gap-3 rounded-gl border border-dashed border-border p-3 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-gl-icon border border-dashed border-border bg-muted text-muted-foreground">
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-foreground">
            Custom integration
          </span>
          <span className="block text-[11.5px] text-muted-foreground">
            Configure a generic HTTP integration by hand.
          </span>
        </span>
      </button>
    </div>
  );
}
